import { Component } from 'react';
import PropTypes from 'prop-types';
import braintreeClient from 'braintree-web/client';
import { connect } from 'react-redux';
import get from 'lodash.get';
import pick from 'lodash.pick';
import { getGuestUserId } from '../../helpers/checkoutGuestUserToken';
import { isVerifyingCreditCard, isAddingCreditCard } from '../../reducers/checkoutPaymentReducer';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { TABS, STEPS, PAYMENTS } from '../../constants/checkoutConstants';
import { trackCompletedStep, trackFormErrors } from '../../helpers/checkoutTracking';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { paymentMethodIsPermissible } from '../helpers/paymentMethodIsPermissible';
import {
    billingFields,
    getTokenizeCardFormat,
    getAddressErrors,
    getCreditCardErrors,
    getErrorMessages,
} from '../../helpers/checkoutForms';
import {
    creditCardSaved,
    creditCardVaultedError,
    togglePaymentSaving,
    completeCreditCards,
} from '../../actions/checkoutPaymentActions';
import { setTriggeredErrors } from '../../actions/checkoutUIActions';
import { shouldVerifyPaymentMethod } from '../../helpers/checkoutRedirects';
import { guestCheckoutWalletUpdater } from '../helpers/guestCheckoutWalletUpdater';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { tradeFirmUpdateEoriVat } from '../../mutations/TradeFirmUpdateEoriVatMutation';

import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';

import CheckoutSaveAndContinueAction from '../../components/shared/actions/CheckoutSaveAndContinueAction';
import CheckoutContinueAction from '../../components/shared/actions/CheckoutContinueAction';
import CheckoutCTAButton from '../../components/shared/CheckoutCTAButton';

import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';

export const filterPaymentMethodsProxies = ({
    paymentMethodsProxies = [],
    paymentMethodTypeToRemove,
}) =>
    paymentMethodsProxies.filter(
        paymentMethodsProxy =>
            paymentMethodsProxy.getLinkedRecord('paymentMethod').getValue('__typename') !==
            paymentMethodTypeToRemove
    );

function formatAddress(address) {
    return {
        firstName: address?.firstName,
        lastName: address?.lastName,
        country: address?.country,
        line1: address?.line1,
        line2: address?.line2,
        city: address?.city,
        state: address?.state,
        zipCode: address?.zipCode,
        company: address?.company,
    };
}

class CheckoutCreditCardSubmit extends Component {
    constructor(props) {
        super(props);
        this.onSubmit = this.onSubmit.bind(this);
        this.onComplete = this.onComplete.bind(this);
    }
    isDisabled(isValid) {
        const {
            buyer,
            shippingAddressId,
            paymentMethodToken,
            lastValidCreditCardToken,
            isPaymentSaving,
            isAdding,
            isVerifying,
        } = this.props;

        if (isPaymentSaving) {
            return true;
        }
        if (isAdding || isVerifying) {
            return !isValid;
        }
        const paymentMethods = get(buyer, 'wallet.paymentMethods') || [];
        const walletPaymentMethod = paymentMethods.find(
            ({ paymentMethod }) =>
                get(paymentMethod, '__typename') === PAYMENTS.TYPE_CREDIT_CARD &&
                get(paymentMethod, 'token') === (lastValidCreditCardToken || paymentMethodToken)
        );
        const hasPermissibleCards = paymentMethodIsPermissible(
            walletPaymentMethod,
            shippingAddressId
        );
        const isExpired = walletPaymentMethod?.paymentMethod?.isExpired;
        return isExpired || !hasPermissibleCards;
    }
    onComplete(event) {
        const {
            onCompleteCreditCards,
            paymentMethodApiType,
            paymentMethodToken,
            lastValidCreditCardToken,
        } = this.props;
        event.preventDefault();

        if (!this.isDisabled()) {
            trackCompletedStep(STEPS.STEP_PAYMENT);
            onCompleteCreditCards({
                paymentMethodApiType,
                paymentMethodToken,
                lastValidCreditCardToken,
            });
        }
    }
    validation() {
        const {
            isBillingSameAsShipping,
            newCardForm,
            newBillingForm,
            verifyCardForm,
            isVerifying,
        } = this.props;
        const cardForm = isVerifying ? verifyCardForm : newCardForm;
        const { isValid: isValidCard, errors: cardErrors } = getCreditCardErrors(
            cardForm,
            isVerifying
        );
        const { isValid: isValidAddress, errors: addressErrors } =
            isBillingSameAsShipping || isVerifying
                ? { errors: {}, isValid: true }
                : getAddressErrors(newBillingForm, true);
        return {
            isValid: isValidCard && isValidAddress,
            errors: { ...cardErrors, ...addressErrors },
        };
    }
    getCurrentShippingAddress() {
        const { buyer, shippingAddressId } = this.props;
        return formatAddress(
            (buyer?.addressBook?.addressList || []).find(a => a.serviceId === shippingAddressId)
        );
    }
    async onSubmit({ isValid, errors, isPaymentSaving }) {
        if (!isValid) {
            const { onValidationError, onTriggeredErrors } = this.props;
            onValidationError({ isValid, errors });
            trackFormErrors({
                step: STEPS.STEP_PAYMENT,
                errors: getErrorMessages(errors),
            });
            if (errors && Object.keys(errors).length) {
                onTriggeredErrors(Object.keys(errors));
            }
            return null;
        } else if (isPaymentSaving) {
            return null;
        }

        const {
            relay,
            viewer,
            buyer: user,
            newCardForm,
            newBillingForm,
            verifyCardForm,
            isVerifying,
            onCreditCardSaved,
            isBillingSameAsShipping,
            onTogglePaymentSaving,
            creditCardToken,
            eoriNumber,
            vatId,
        } = this.props;
        const { isMobile } = this.context;

        const clientToken = viewer?.clientToken?.value;
        const walletPaymentMethods = user?.wallet?.paymentMethods || [];
        const guestUserId = getGuestUserId();
        const walletPaymentMethod = walletPaymentMethods.find(
            ({ paymentMethod }) => paymentMethod.token === creditCardToken
        );
        const currentCreditCard = walletPaymentMethod?.paymentMethod;
        const cardData = {
            ...(isVerifying ? verifyCardForm : newCardForm),
            billingAddress:
                isBillingSameAsShipping && !isVerifying
                    ? pick(this.getCurrentShippingAddress(), billingFields)
                    : formatAddress(currentCreditCard?.billingAddress || newBillingForm),
        };
        const creditCard = getTokenizeCardFormat({ cardData, isVerifying });
        onTogglePaymentSaving(true);

        try {
            const tradeFirmId = user?.tradeFirm?.serviceId;
            if (tradeFirmId && (eoriNumber || vatId)) {
                await tradeFirmUpdateEoriVat(relay.environment, {
                    serviceId: tradeFirmId,
                    eoriNumber,
                    vatId,
                });
            }

            const captchaResults = await executeCaptcha({
                action: 'checkoutVaultCreditCard',
                featureFlag: 'vaultCreditCardCaptcha',
                featureFlagV3: 'vaultCreditCardCaptchaV3',
                relayEnvironment: relay.environment,
            });
            const clientInstance = await braintreeClient.create({
                authorization: clientToken,
            });
            const braintreeResponse = await clientInstance.request({
                endpoint: 'payment_methods/credit_cards',
                method: 'post',
                data: {
                    creditCard,
                    options: {
                        validate: false,
                    },
                },
            });

            // Probs need to add paymentMethod to the wallet if they log in before completeling checkout
            const vaultResponse = await CheckoutMutationWrapper.commit(
                'UserVaultPaymentMethodMutation',
                relay.environment,
                {
                    userId: user.serviceId,
                    saveToWallet: !guestUserId,
                    mockWallet: !!guestUserId,
                    paymentMethodNonce: get(braintreeResponse, 'creditCards[0].nonce'),
                    verifyPaymentMethod: shouldVerifyPaymentMethod(),
                    captchaToken: captchaResults?.captchaToken,
                    captchaKey: captchaResults?.captchaKey,
                },
                isMobile,
                guestUserId
                    ? store =>
                          guestCheckoutWalletUpdater(store, viewer.id, 'userVaultPaymentMethod')
                    : undefined
            );

            const vaultedPaymentMethod =
                vaultResponse?.userVaultPaymentMethod?.walletPaymentMethod?.paymentMethod || {};
            const { token, paymentProcessor } = vaultedPaymentMethod;

            trackCompletedStep(STEPS.STEP_PAYMENT);
            onCreditCardSaved({ token, paymentProcessor });
            onTogglePaymentSaving(false);
        } catch (error) {
            logCheckoutError(error, 'vault');
            this.props.onCreditCardVaultedError('VAULT');
            onTogglePaymentSaving(false);
        }
        return null;
    }
    render() {
        const { paymentTab, isAdding, isPaymentSaving, isVerifying, isPaymentLoading } = this.props;
        const { isValid, errors } = paymentTab === TABS.TAB_CREDIT_CARD ? this.validation() : {};
        const onClick =
            isAdding || isVerifying
                ? () => this.onSubmit({ isValid, errors, isPaymentSaving })
                : this.onComplete;
        const submitLabel = isAdding ? (
            <CheckoutSaveAndContinueAction />
        ) : (
            <CheckoutContinueAction />
        );

        return (
            <CheckoutCTAButton
                onClick={onClick}
                label={submitLabel}
                isSaving={isPaymentSaving}
                disabled={isPaymentSaving || isPaymentLoading}
                marginTop
                dataTn={`creditCard-save-button${isPaymentSaving ? '-disabled' : ''}`}
            />
        );
    }
}

CheckoutCreditCardSubmit.propTypes = {
    isVerifying: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    onCreditCardSaved: PropTypes.func.isRequired,
    onCreditCardVaultedError: PropTypes.func.isRequired,
    shippingAddressId: PropTypes.string.isRequired,
    isBillingSameAsShipping: PropTypes.bool.isRequired,
    newCardForm: PropTypes.object.isRequired,
    newBillingForm: PropTypes.object.isRequired,
    verifyCardForm: PropTypes.object.isRequired,
    paymentTab: PropTypes.oneOf(Object.values(TABS)),
    paymentMethodToken: PropTypes.string,
    paymentMethodApiType: PropTypes.string,
    lastValidCreditCardToken: PropTypes.string,
    isPaymentSaving: PropTypes.bool,
    onCompleteCreditCards: PropTypes.func.isRequired,
    onTogglePaymentSaving: PropTypes.func.isRequired,
    isAdding: PropTypes.bool,
    onValidationError: PropTypes.func.isRequired,
    creditCardToken: PropTypes.string,
    eoriNumber: PropTypes.string,
    vatId: PropTypes.string,
    isPaymentLoading: PropTypes.bool,
    onTriggeredErrors: PropTypes.func.isRequired,
};

CheckoutCreditCardSubmit.contextType = CheckoutServerVarsContext;

const ConnectedCheckoutCreditCardSubmit = connect(
    ({ transaction, payment }) => {
        const isVerifying = isVerifyingCreditCard(payment);
        return {
            shippingAddressId: transaction.shippingAddressId,
            newCardForm: payment.newCardForm,
            newBillingForm: payment.newBillingForm,
            verifyCardForm: payment.verifyCardForm,
            isVerifying: isVerifyingCreditCard(payment),
            isBillingSameAsShipping: payment.isBillingSameAsShipping,
            isPaymentSaving: payment.isPaymentSaving,
            paymentTab: payment.paymentTab,
            isAdding: isAddingCreditCard(payment),
            lastValidCreditCardToken: payment.lastValidCreditCardToken,
            paymentMethodToken: transaction.paymentMethodToken,
            paymentMethodApiType: transaction.paymentMethodApiType,
            creditCardToken: isVerifying ? payment.verifyCardToken : payment.creditCardToken,
            eoriNumber: payment.newEoriNumber,
            vatId: payment.newVatId,
        };
    },
    {
        onCreditCardSaved: creditCardSaved,
        onCreditCardVaultedError: creditCardVaultedError,
        onTogglePaymentSaving: togglePaymentSaving,
        onCompleteCreditCards: completeCreditCards,
        onTriggeredErrors: setTriggeredErrors,
    }
)(CheckoutCreditCardSubmit);

export default createFragmentContainer(ConnectedCheckoutCreditCardSubmit, {
    viewer: graphql`
        fragment CheckoutCreditCardSubmit_viewer on Viewer {
            id
            clientToken {
                value
            }
        }
    `,
    buyer: graphql`
        fragment CheckoutCreditCardSubmit_buyer on User {
            id
            serviceId
            wallet {
                paymentMethods {
                    ...paymentMethodIsPermissible_paymentMethod
                    paymentMethod {
                        __typename
                        ... on CreditCard {
                            token
                            isExpired
                            billingAddress {
                                firstName
                                lastName
                                line1
                                line2
                                city
                                state
                                zipCode
                                country
                                company
                            }
                        }
                    }
                }
            }
            addressBook {
                addressList {
                    serviceId
                    firstName
                    lastName
                    country
                    line1
                    line2
                    city
                    state
                    zipCode
                }
            }
            tradeFirm {
                serviceId
            }
        }
    `,
});
